import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { KlearDate } from '@klear/date'
import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  ROUTE_DISBURSEMENTS,
  ROUTE_DISBURSE,
  ROUTE_PAYMENT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ENTITY_INVOICE_OFFER
} from '@klear/constants'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { Plus, RotateCcw } from 'lucide-react'
import {
  PaymentTypeOptions,
  PAYMENT_TYPE_DISBURSEMENT_FEE,
  PAYMENT_TYPE_DISBURSEMENT_ADVANCE,
  type PaymentType
} from '@klear/payment/constants'
import { AgGridReact } from 'ag-grid-react'
import {
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
  type ICellRendererParams
} from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { trpc } from '@klear/ui/lib/trpc'
import {
  useListDisbursementPayments,
  useAllDisbursableAdvances,
  type DisbursableAdvanceRow,
  type DisbursementRow
} from '@klear/ui/hooks/use-disbursements'
import {
  PAAS_SCOPE,
  PAAS_SCOPE_OPTIONS,
  paasScopeFilters,
  usePaasPositions,
  type PaasScope
} from '@klear/ui/hooks/use-paas'
import PaasPositionsGrid from '@/components/PaasPositionsGrid'
import SyncStripePaymentsButton from '@/components/SyncStripePaymentsButton'
import ViewIcon from '@/icons/ViewIcon'
import ExportIcon from '@/icons/ExportIcon'
import { byPaymentCreatedAtDesc } from '@/lib/payment-sort'
import { DISBURSEMENT_STEP_ID } from '@/app/disbursements/steps'
import { useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { paymentIsFailed, paymentIsScheduled } from '@klear/payment/status'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const PAYMENT_TAB_ALL = 'all'
const PAYMENT_TAB_PAAS = 'paas'

type DisbursementFilterType =
  | typeof PAYMENT_TYPE_DISBURSEMENT_ADVANCE
  | typeof PAYMENT_TYPE_DISBURSEMENT_FEE
  | typeof PAYMENT_TAB_ALL
  | typeof PAYMENT_TAB_PAAS

const FILTER_OPTIONS: { value: DisbursementFilterType; label: string }[] = [
  { value: PAYMENT_TAB_ALL, label: 'Disbursable' },
  { value: PAYMENT_TYPE_DISBURSEMENT_ADVANCE, label: 'Advances' },
  { value: PAYMENT_TYPE_DISBURSEMENT_FEE, label: 'Fees' },
  { value: PAYMENT_TAB_PAAS, label: 'PaaS' }
]

const FILTER_QUERY_PARAM = 'filter'
const DEFAULT_FILTER: DisbursementFilterType = PAYMENT_TAB_ALL
const ROW_BG_SCHEDULED = 'rgb(254 249 195)'
const ROW_BG_FAILED = 'rgb(254 202 202)'

function buildDisburseStartHref(nextStep: string, traderId?: number): string {
  const base = `/${ROUTE_DISBURSEMENTS}/${ROUTE_DISBURSE}/${nextStep}`
  return traderId != null ? `${base}?traderId=${traderId}` : base
}

const isDisbursementFilterType = (value: string | null): value is DisbursementFilterType =>
  value != null && FILTER_OPTIONS.some((opt) => opt.value === value)

function fundingSpvLabelFromDisbursementRow(row: Pick<DisbursementRow, 'invoiceOffers'>): string {
  const offer = row.invoiceOffers[0]
  return offer?.fundingSpvLabel?.trim() || '—'
}

function customerNamesFromInvoiceOffers(offers: InvoiceOffer[]): string {
  const names = [
    ...new Set(
      offers.map((offer) => offer.customerName?.trim()).filter((name): name is string => !!name && name.length > 0)
    )
  ].sort()
  return names.join(', ')
}

function DisbursableAdvanceAction(props: ICellRendererParams<DisbursableAdvanceRow>) {
  const row = props.data
  if (!row) return null
  if (row.fundBlocker != null) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="h-5 cursor-help border-transparent bg-amber-100 px-1.5 py-0 text-[11px] font-medium text-amber-900 shadow-none hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200">
              Waiting for diligence
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            {row.fundBlocker}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  return (
    <Button variant="outline" size="xs" asChild>
      <Link
        to={buildDisburseStartHref(DISBURSEMENT_STEP_ID.SELECT_ITEMS, row.offer.traderId)}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        Disburse
      </Link>
    </Button>
  )
}

export default function Disbursements() {
  useDocumentTitle('Disbursements')
  const { user } = useAuth()
  const navigate = useNavigate()
  const agGrid = useKlearAgGrid()
  const [gridApi, setGridApi] = useState<GridApi<DisbursementRow> | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParam = searchParams.get(FILTER_QUERY_PARAM)
  const activeFilter: DisbursementFilterType = isDisbursementFilterType(filterParam) ? filterParam : DEFAULT_FILTER

  const setActiveFilter = (value: DisbursementFilterType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === DEFAULT_FILTER) {
          next.delete(FILTER_QUERY_PARAM)
        } else {
          next.set(FILTER_QUERY_PARAM, value)
        }
        return next
      },
      { replace: true }
    )
  }

  const isAllTab = activeFilter === PAYMENT_TAB_ALL
  const isPaasTab = activeFilter === PAYMENT_TAB_PAAS

  const utils = trpc.useUtils()

  const { disbursements: allDisbursements, isLoading: isDisbursementsLoading } = useListDisbursementPayments(
    !isAllTab && !isPaasTab
  )
  const { disbursableAdvances, isLoading: isDisbursableLoading } = useAllDisbursableAdvances(isAllTab)
  const [paasScope, setPaasScope] = useState<PaasScope>(PAAS_SCOPE.ALL)
  const { positions: paasPositions, isLoading: isPaasLoading } = usePaasPositions(
    paasScopeFilters(paasScope),
    isPaasTab
  )

  const sortedDisbursableAdvances = useMemo(
    () => [...disbursableAdvances].sort((a, b) => b.offer.id - a.offer.id),
    [disbursableAdvances]
  )

  const { mutate: retryPayment, isPending: isRetrying } = useMutationWithToast(
    trpc.payment.retry.useMutation,
    {
      successTitle: 'Payment scheduled for retry',
      errorTitle: 'Failed to retry payment'
    },
    {
      onSuccess: () => {
        utils.disbursement.listDisbursementPayments.invalidate()
      }
    }
  )

  const ActionCellRenderer = useCallback(
    (props: ICellRendererParams<DisbursementRow>) => {
      const row = props.data
      if (!row) return null
      const isFailedAdvance =
        paymentIsFailed(row.payment.status) && row.payment.paymentType === PAYMENT_TYPE_DISBURSEMENT_ADVANCE

      return (
        <div className="flex items-center gap-2">
          <Link
            to={`/${ROUTE_PAYMENT}/${row.payment.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <ViewIcon />
            View
          </Link>
          {isFailedAdvance && (
            <button
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
              disabled={isRetrying}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                retryPayment({ id: row.payment.id! })
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
        </div>
      )
    },
    [retryPayment, isRetrying]
  )

  const isLoading = isAllTab ? isDisbursableLoading : isDisbursementsLoading

  const disbursements = useMemo<DisbursementRow[]>(() => {
    if (!allDisbursements) return []
    const filtered =
      activeFilter === PAYMENT_TAB_ALL
        ? allDisbursements
        : allDisbursements.filter((row) => row.payment.paymentType === activeFilter)
    return [...filtered].sort(byPaymentCreatedAtDesc)
  }, [allDisbursements, activeFilter])

  const columnDefs = useMemo<ColDef<DisbursementRow>[]>(
    () => [
      {
        headerName: 'ID',
        colId: 'id',
        type: 'text',
        valueGetter: (params: ValueGetterParams<DisbursementRow>) => {
          const row = params.data
          if (!row) return ''
          if (row.invoiceOffers.length === 0) return row.payment.id ?? ''
          return row.invoiceOffers.map((offer) => offer.idDisplay).join(', ')
        },
        width: 120,
        minWidth: 120
      },
      {
        headerName: 'Date',
        colId: 'effectiveDate',
        field: 'payment.effectiveDate'
      },
      {
        headerName: 'Amount',
        colId: 'amount',
        field: 'payment.amount'
      },
      {
        headerName: 'Type',
        colId: 'paymentType',
        type: 'text',
        valueGetter: (params: ValueGetterParams<DisbursementRow>) => {
          const type = params.data?.payment.paymentType
          if (type == null) return ''
          return PaymentTypeOptions.find((opt) => opt.value === (type as PaymentType))?.label ?? type
        }
      },
      {
        headerName: 'Customer',
        colId: 'customerName',
        type: 'text',
        valueGetter: (params: ValueGetterParams<DisbursementRow>) =>
          params.data ? customerNamesFromInvoiceOffers(params.data.invoiceOffers) : '',
        valueFormatter: (params: ValueFormatterParams<DisbursementRow>) => (params.value as string) || '-'
      },
      {
        headerName: 'Funding SPV',
        colId: 'fundingSpv',
        type: 'text',
        valueGetter: (params: ValueGetterParams<DisbursementRow>) =>
          params.data ? fundingSpvLabelFromDisbursementRow(params.data) : ''
      },
      {
        headerName: 'From',
        colId: 'sourceAccountOrganizationName',
        field: 'payment.sourceAccountOrganizationName'
      },
      {
        headerName: 'To',
        colId: 'destinationAccountOrganizationName',
        field: 'payment.destinationAccountOrganizationName'
      },
      {
        headerName: 'Status',
        colId: 'status',
        field: 'payment.status'
      },
      {
        headerName: 'Created by',
        colId: 'createdByEmail',
        field: 'payment.createdByEmail'
      },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        pinned: 'right',
        cellStyle: { overflow: 'visible' },
        width: 150,
        minWidth: 150,
        maxWidth: 150,
        cellRenderer: ActionCellRenderer
      }
    ],
    [ActionCellRenderer]
  )

  const disbursableColumnDefs = useMemo<ColDef<DisbursableAdvanceRow>[]>(
    () => [
      {
        headerName: 'Trader',
        colId: 'traderName',
        field: 'offer.traderName'
      },
      {
        headerName: 'Customer',
        colId: 'customerName',
        field: 'offer.customerName',
        valueFormatter: (params: ValueFormatterParams<DisbursableAdvanceRow>) => (params.value as string) || '-'
      },
      {
        headerName: 'Offer',
        colId: 'idDisplay',
        field: 'offer.idDisplay'
      },
      {
        headerName: 'Funding SPV',
        colId: 'fundingSpv',
        field: 'offer.fundingSpvLabel',
        valueFormatter: (params: ValueFormatterParams<DisbursableAdvanceRow>) =>
          params.data?.offer.fundingSpvLabel?.trim() || '—'
      },
      {
        headerName: 'Amount',
        colId: 'amount',
        field: 'offer.purchasePrice',
        type: 'rightAligned'
      },
      {
        headerName: 'Recovery date',
        colId: 'endDate',
        field: 'offer.endDate'
      },
      {
        headerName: 'Fund status',
        colId: 'fundBlocker',
        field: 'fundBlocker',
        pinned: 'right',
        cellStyle: { overflow: 'visible' },
        width: 190,
        minWidth: 190,
        maxWidth: 190,
        valueFormatter: (params: ValueFormatterParams<DisbursableAdvanceRow>) => params.data?.fundBlocker ?? 'Ready',
        cellRenderer: DisbursableAdvanceAction
      }
    ],
    []
  )

  const defaultColDef = useMemo(() => ({ ...agGrid.defaultColDef, flex: 1 }), [agGrid.defaultColDef])

  const onGridReady = (params: GridReadyEvent<DisbursementRow>) => {
    setGridApi(params.api)
  }

  const onDisbursableGridReady = (params: GridReadyEvent<DisbursableAdvanceRow>) => {
    setGridApi(params.api as unknown as GridApi<DisbursementRow>)
  }

  const handleExport = () => {
    if (!gridApi) return
    if (isAllTab) {
      gridApi.exportDataAsCsv({
        fileName: `disbursable_advances_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: ['traderName', 'customerName', 'idDisplay', 'fundingSpv', 'amount', 'endDate', 'fundBlocker']
      })
      return
    }
    gridApi.exportDataAsCsv({
      fileName: `disbursements_export_${KlearDate.today().toJSON()}.csv`,
      columnKeys: [
        'id',
        'effectiveDate',
        'amount',
        'paymentType',
        'customerName',
        'fundingSpv',
        'sourceAccountOrganizationName',
        'destinationAccountOrganizationName',
        'status',
        'createdByEmail'
      ]
    })
  }

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Disbursements</h2>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {FILTER_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={activeFilter === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <SyncStripePaymentsButton />
          <Button
            onClick={handleExport}
            variant="outline"
            disabled={!gridApi || (isAllTab ? disbursableAdvances.length === 0 : disbursements.length === 0)}
          >
            <ExportIcon />
            <span>Export CSV</span>
          </Button>
          {user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_FUND) && (
            <Button
              onClick={() => navigate(buildDisburseStartHref(DISBURSEMENT_STEP_ID.SELECT_TRADER))}
              variant="default"
            >
              <Plus className="w-4 h-4 mr-2" />
              Disburse advances
            </Button>
          )}
        </div>
      </div>
      {isPaasTab ? (
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          <div className="flex items-center gap-1">
            {PAAS_SCOPE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={paasScope === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPaasScope(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <PaasPositionsGrid positions={paasPositions} isLoading={isPaasLoading} variant="disbursements" />
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="w-full h-full min-h-[600px]">
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">Loading...</div>
            ) : isAllTab ? (
              <div className="flex flex-col gap-4 h-full">
                {disbursableAdvances.length === 0 ? (
                  <div className="flex items-center justify-center flex-1 text-muted-foreground">
                    No disbursable advances.
                  </div>
                ) : (
                  <div className="flex-1 min-h-[300px]">
                    <AgGridReact<DisbursableAdvanceRow>
                      key="disbursable-advances-grid"
                      rowHeight={35}
                      headerHeight={40}
                      rowData={sortedDisbursableAdvances}
                      columnDefs={disbursableColumnDefs}
                      defaultColDef={defaultColDef}
                      columnTypes={agGrid.columnTypes}
                      dataTypeDefinitions={agGrid.dataTypeDefinitions}
                      suppressCellFocus={true}
                      animateRows={true}
                      enableCellTextSelection={false}
                      pagination={true}
                      paginationPageSize={20}
                      theme={agGrid.theme}
                      quickFilterText=""
                      onGridReady={onDisbursableGridReady}
                    />
                  </div>
                )}
              </div>
            ) : disbursements.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No disbursements found.
              </div>
            ) : (
              <AgGridReact<DisbursementRow>
                key="disbursements-grid"
                rowHeight={35}
                headerHeight={40}
                rowData={disbursements}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                columnTypes={agGrid.columnTypes}
                dataTypeDefinitions={agGrid.dataTypeDefinitions}
                suppressCellFocus={true}
                animateRows={true}
                enableCellTextSelection={false}
                pagination={true}
                paginationPageSize={20}
                theme={agGrid.theme}
                quickFilterText=""
                onGridReady={onGridReady}
                getRowStyle={(params) => {
                  const status = params.data?.payment?.status
                  if (!status) return undefined
                  if (paymentIsScheduled(status)) return { backgroundColor: ROW_BG_SCHEDULED }
                  if (paymentIsFailed(status)) return { backgroundColor: ROW_BG_FAILED }
                  return undefined
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
